import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import config from "../config";
import "./LocationPicker.css";

function deduplicateCities(cities) {
  const seen = new Set();
  return cities.filter((c) => {
    const key = `${c.name}::${c.stateAbbr}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function citiesInBounds(cityIndex, bounds) {
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const west = bounds.getWest();
  const east = bounds.getEast();
  return cityIndex.filter((c) => {
    // If bounding box data exists, check if the city's polygon bbox
    // intersects the drawn rectangle (any land overlap).
    if (c.minLat !== undefined) {
      return !(c.minLat > north || c.maxLat < south || c.minLon > east || c.maxLon < west);
    }
    // Fallback to center-point check if no bbox data
    return c.lat >= south && c.lat <= north && c.lon >= west && c.lon <= east;
  });
}

function DrawControl({ cityIndex, onRectanglesChange }) {
  const map = useMap();
  const drawnItemsRef = useRef(null);
  const controlRef = useRef(null);

  useEffect(() => {
    if (!map || controlRef.current) return;

    const drawnItems = new L.FeatureGroup();
    drawnItemsRef.current = drawnItems;
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        rectangle: {
          shapeOptions: {
            color: "#3469ff",
            weight: 2,
            fillOpacity: 0.1,
          },
        },
        polygon: false,
        circle: false,
        circlemarker: false,
        marker: false,
        polyline: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
        edit: false,
      },
    });
    controlRef.current = drawControl;
    map.addControl(drawControl);

    const recalculate = () => {
      if (!cityIndex) return;
      const rects = [];
      drawnItems.eachLayer((layer) => {
        const bounds = layer.getBounds();
        const matched = citiesInBounds(cityIndex, bounds);
        rects.push({ bounds, cities: matched });
      });
      onRectanglesChange(rects);
    };

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnItems.addLayer(e.layer);
      recalculate();
    });

    map.on(L.Draw.Event.DELETED, () => {
      recalculate();
    });

    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      controlRef.current = null;
    };
    // cityIndex and onRectanglesChange are stable refs — only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Re-bind the recalculate when cityIndex loads after the control is already mounted
  useEffect(() => {
    if (!map || !drawnItemsRef.current || !cityIndex) return;

    const recalculate = () => {
      const rects = [];
      drawnItemsRef.current.eachLayer((layer) => {
        const bounds = layer.getBounds();
        const matched = citiesInBounds(cityIndex, bounds);
        rects.push({ bounds, cities: matched });
      });
      onRectanglesChange(rects);
    };

    map.off(L.Draw.Event.CREATED);
    map.off(L.Draw.Event.DELETED);

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnItemsRef.current.addLayer(e.layer);
      recalculate();
    });

    map.on(L.Draw.Event.DELETED, () => {
      recalculate();
    });

    // Match any rectangles drawn before cityIndex finished loading.
    recalculate();
  }, [map, cityIndex, onRectanglesChange]);

  return null;
}

function LocationPicker({ onCitiesChange, enabled = true }) {
  const [cityIndex, setCityIndex] = useState(null);
  const [rectangles, setRectangles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const fetchCities = async () => {
      setIsLoading(true);
      try {
        const resp = await fetch(
          `${config.api.baseUrl}/gpt-seo/city-index`,
          { credentials: "include" }
        );
        const data = await resp.json();
        if (!cancelled && data.success) {
          setCityIndex(data.cities);
        }
      } catch (err) {
        console.error("Failed to load city index:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchCities();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const handleRectanglesChange = useCallback(
    (rects) => {
      setRectangles(rects);
      const allCities = deduplicateCities(rects.flatMap((r) => r.cities));
      onCitiesChange(allCities);
    },
    [onCitiesChange]
  );

  const uniqueCities = deduplicateCities(rectangles.flatMap((r) => r.cities));

  return (
    <div className="location-picker">
      <div className="location-picker-header">
        <h2>Location Filter (Optional)</h2>
        <span className="location-picker-hint">
          Draw rectangles on the map to target specific geographic areas
        </span>
      </div>
      {/* Render the map immediately; city-index arrives asynchronously and
          wires itself into DrawControl via its useEffect. Showing the map
          first makes the form feel responsive even when the /city-index
          fetch is slow. */}
      <div className="location-picker-map-wrap">
        <MapContainer
          center={[39.8, -98.5]}
          zoom={4}
          className="location-picker-map"
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <DrawControl
            cityIndex={cityIndex}
            onRectanglesChange={handleRectanglesChange}
          />
        </MapContainer>
        {isLoading && (
          <div className="location-picker-loading-badge">
            Loading cities…
          </div>
        )}
      </div>
      {uniqueCities.length > 0 && (
        <div className="location-picker-summary">
          <strong>
            {rectangles.length} region(s) selected &mdash; {uniqueCities.length}{" "}
            cities
          </strong>
          <div className="location-picker-city-list">
            {uniqueCities.slice(0, 50).map((c) => (
              <span
                key={`${c.name}-${c.stateAbbr}`}
                className="location-picker-city-tag"
              >
                {c.name}, {c.stateAbbr}
              </span>
            ))}
            {uniqueCities.length > 50 && (
              <span className="location-picker-city-tag">
                +{uniqueCities.length - 50} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationPicker;
